const AboutMagicApps = () => {
  return (
    <div className="legal-page">
      <h1>About EPS Viewer</h1>

      <p>
        EPS Viewer is a free online tool built by{" "}
        <a href="https://magicapps.co.in/" target="_blank" rel="noopener noreferrer">MagicApps</a> that
        lets anyone upload an EPS file and instantly preview or convert it to PNG, JPG, or PDF —
        directly in the browser, with no account required and no software to install.
      </p>

      <p>
        We built EPS Viewer because opening an EPS file without Adobe Illustrator — which costs a
        significant monthly subscription — is unnecessarily difficult for most people. Designers,
        clients, print shops, and everyday users were stuck either buying expensive software or
        struggling with unreliable workarounds just to preview a simple vector file. EPS Viewer
        was created to fix that.
      </p>

      <h2>Why We Built This</h2>

      <p>
        The EPS format has been around since the late 1980s and is still widely used in professional
        print and branding work. Yet outside of Adobe Illustrator, there's almost no good way to open
        these files on Windows, Mac, or mobile devices. The same question kept coming up in forums
        and support threads: <em>"How do I open an EPS file without Illustrator?"</em>
      </p>

      <p>
        That gap is what motivated us to build EPS Viewer — a fast, private, and completely free tool
        that works on any device. No installation. No account. No watermarks.
      </p>

      <h2>How It Works</h2>

      <p>
        EPS Viewer processes your file entirely on our secure servers and returns a high-quality preview
        within seconds. Your file is never stored permanently — it is automatically deleted after your
        session ends. All transfers use HTTPS encryption for your privacy and security.
      </p>

      <p>
        You can download your converted file as:
      </p>
      <ul>
        <li><strong>PNG</strong> — ideal for logos and graphics that need a transparent background</li>
        <li><strong>JPG</strong> — compact, web-friendly images for solid-background artwork</li>
        <li><strong>PDF</strong> — print-ready documents accepted universally by print shops</li>
      </ul>

      <h2>About MagicApps</h2>

      <p>
        EPS Viewer is a product of{" "}
        <a href="https://magicapps.co.in/" target="_blank" rel="noopener noreferrer">MagicApps</a>,
        a small app development studio based in India. We build practical, free tools for working with
        design files — including viewers and converters for EPS, CDR, PSD, and AI formats — available
        on the web, Android, and iOS.
      </p>

      <p>
        Our focus is simple: solve real problems for real users, without paywalls or unnecessary
        complexity.
      </p>

      <h2>Business Details</h2>

      <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "480px", fontSize: "0.93rem" }}>
        <tbody>
          <tr>
            <td style={{ padding: "8px 12px 8px 0", fontWeight: "600", whiteSpace: "nowrap" }}>Legal Name</td>
            <td style={{ padding: "8px 0" }}>MagicApps</td>
          </tr>
          <tr>
            <td style={{ padding: "8px 12px 8px 0", fontWeight: "600", whiteSpace: "nowrap" }}>GSTIN</td>
            <td style={{ padding: "8px 0" }}>09ABRFM3601P1ZN</td>
          </tr>
          <tr>
            <td style={{ padding: "8px 12px 8px 0", fontWeight: "600", whiteSpace: "nowrap" }}>Constitution</td>
            <td style={{ padding: "8px 0" }}>Partnership Firm</td>
          </tr>
          <tr>
            <td style={{ padding: "8px 12px 8px 0", fontWeight: "600", whiteSpace: "nowrap" }}>Registered Since</td>
            <td style={{ padding: "8px 0" }}>October 2021</td>
          </tr>
          <tr>
            <td style={{ padding: "8px 12px 8px 0", fontWeight: "600", whiteSpace: "nowrap" }}>Address</td>
            <td style={{ padding: "8px 0" }}>G-116, Delta 2, Greater Noida, Gautam Buddha Nagar, Uttar Pradesh – 201308, India</td>
          </tr>
        </tbody>
      </table>

      <h2>Get in Touch</h2>

      <p>
        Have a question, found a bug, or want to suggest a feature? Reach us at{" "}
        <a href="mailto:support@magicapps.co.in?subject=EPS%20Viewer%20Feedback">support@magicapps.co.in</a>{" "}
        and we'll get back to you within 1–2 business days.
      </p>

      <h2>Our Other Tools</h2>
      <p>We also build apps for other design file formats you might find useful:</p>

      <h3>Android (Google Play)</h3>
      <ul>
        <li><a href="https://play.google.com/store/apps/details?id=com.viewer.eps" target="_blank" rel="noopener noreferrer">EPS Viewer</a></li>
        <li><a href="https://play.google.com/store/apps/details?id=com.vvorld.cdrviewer" target="_blank" rel="noopener noreferrer">CDR Viewer</a></li>
        <li><a href="https://play.google.com/store/apps/details?id=com.psd.viewer" target="_blank" rel="noopener noreferrer">PSD Viewer</a></li>
        <li><a href="https://play.google.com/store/apps/details?id=com.ai.viewer.illustrator" target="_blank" rel="noopener noreferrer">AI Viewer</a></li>
        <li><a href="https://play.google.com/store/apps/details?id=com.vvorld.fileconverter" target="_blank" rel="noopener noreferrer">File to PDF Converter</a></li>
        <li><a href="https://play.google.com/store/apps/details?id=com.vvorld.speed" target="_blank" rel="noopener noreferrer">Flight Speed</a></li>
      </ul>

      <h3>iOS (App Store)</h3>
      <ul>
        <li><a href="https://apps.apple.com/us/app/eps-viewer/id6756544460" target="_blank" rel="noopener noreferrer">EPS Viewer</a></li>
        <li><a href="https://apps.apple.com/us/app/ai-viewer/id1505083223" target="_blank" rel="noopener noreferrer">AI Viewer</a></li>
        <li><a href="https://apps.apple.com/us/app/cdr-viewer-converter/id6747867117" target="_blank" rel="noopener noreferrer">CDR Viewer &amp; Converter</a></li>
        <li><a href="https://apps.apple.com/us/app/psd-viewer-converter/id6758003380" target="_blank" rel="noopener noreferrer">PSD Viewer &amp; Converter</a></li>
      </ul>
    </div>
  );
};

export default AboutMagicApps;
